import React, { useState, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toWords } from 'number-to-words';
import { v4 as uuidv4 } from 'uuid';
// Make sure you have created and updated this CSS file with the new styles below
import './ProformaInvoice.css';

// --- Constants (Unchanged) ---
const ANANTA_INFO = {
    companyName: 'ANANTA INFOTECH',
    address: 'Dineshpur Udham Singh Nagar, Uttarakhand(263160)',
    contactNo: '7533928185',
    email: 'kishor002912@gmail.com',
    website: 'https://anantainfotech.com',
    bank: {
        holderName: 'Kishor Kumar Sarkar',
        bankName: 'BANK OF BARODA',
        accountNo: '37930100008116',
        ifsc: 'BARB0DINESH',
    },
    defaultPreparedBy: 'Kishor Kumar Sarkar',
    defaultSignatoryName: 'Kishor Kumar Sarkar',
    defaultSignatoryDesignation: 'Proprietor',
};
const LOCAL_STORAGE_KEY = 'anantaProformaInvoiceTemplate_v2'; // Version bump for new structure
const RUPEE_SYMBOL = '₹';

// --- Helper Functions (Unchanged) ---
const formatCurrency = (amount) => {
    const num = Number(amount || 0);
    return `${RUPEE_SYMBOL} ${num.toFixed(2)}`;
};
const sanitizeFilename = (name) => {
    if (!name) return 'Proforma-Invoice';
    return name.replace(/[\\/:*?"<>|]/g, '_').trim() || 'Proforma-Invoice';
};

// --- Component ---
function ProformaInvoice() {
    // --- State & Refs (Unchanged) ---
    const [id, setId] = useState(`PI-${Date.now().toString().slice(-6)}`);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
    const [expiryDate, setExpiryDate] = useState('');
    const [preparedBy, setPreparedBy] = useState(ANANTA_INFO.defaultPreparedBy);
    const [clientName, setClientName] = useState('');
    const [clientCompanyName, setClientCompanyName] = useState('');
    const [clientAddress, setClientAddress] = useState('');
    const [clientPhone, setClientPhone] = useState('');
    const [clientEmail, setClientEmail] = useState('');
    const [lineItems, setLineItems] = useState([{ id: uuidv4(), description: '', quantity: '1', rate: '' }]);
    const [gstRate, setGstRate] = useState('18');
    const [termsValidity, setTermsValidity] = useState('15 Days');
    const [termsDelivery, setTermsDelivery] = useState('Within 10 business days after confirmation');
    const [termsPayment, setTermsPayment] = useState('50% advance, 50% before delivery');
    const [termsShipping, setTermsShipping] = useState('Included');
    const [authorizedName, setAuthorizedName] = useState(ANANTA_INFO.defaultSignatoryName);
    const [authorizedDesignation, setAuthorizedDesignation] = useState(ANANTA_INFO.defaultSignatoryDesignation);
    const [savedInvoices, setSavedInvoices] = useState([]);
    const invoiceRef = useRef();

    // --- Effects & Calculations (Unchanged) ---
    useEffect(() => {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (stored) setSavedInvoices(JSON.parse(stored));
    }, []);

    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(savedInvoices));
    }, [savedInvoices]);

    const subtotal = lineItems.reduce((sum, item) => sum + (Number(item.quantity || 0) * Number(item.rate || 0)), 0);
    const gstAmount = subtotal * (Number(gstRate || 0) / 100);
    const grandTotal = subtotal + gstAmount;

    // --- Event Handlers & Data Management (Unchanged) ---
    const handleInputChange = (setter) => (event) => setter(event.target.value);
    const handleNumericInputChange = (setter) => (event) => {
        const value = event.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) setter(value);
    };
    const handleLineItemChange = (id, field) => (event) => {
        const { value } = event.target;
        setLineItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
    };
    const addLineItem = () => setLineItems([...lineItems, { id: uuidv4(), description: '', quantity: '1', rate: '' }]);
    const removeLineItem = (id) => setLineItems(prev => prev.filter(item => item.id !== id));
    
    // ... all other functions like collectInvoiceData, handleSave, handleLoad, etc. remain the same

    const collectInvoiceData = () => ({
        id, savedAt: new Date().toISOString(), invoiceNumber, invoiceDate, expiryDate, preparedBy,
        clientName, clientCompanyName, clientAddress, clientPhone, clientEmail,
        lineItems, gstRate, termsValidity, termsDelivery, termsPayment, termsShipping,
        authorizedName, authorizedDesignation,
    });

    const handleSave = () => {
        const data = collectInvoiceData();
        setSavedInvoices(prev => {
            const index = prev.findIndex(inv => inv.id === id);
            if (index > -1) {
                const updated = [...prev]; updated[index] = data; return updated;
            }
            return [...prev, data];
        });
        alert('Proforma Invoice saved!');
    };

    const handleLoad = (data) => {
        setId(data.id || `PI-${Date.now().toString().slice(-6)}`);
        setInvoiceNumber(data.invoiceNumber || ''); setInvoiceDate(data.invoiceDate || new Date().toISOString().slice(0, 10));
        setExpiryDate(data.expiryDate || ''); setPreparedBy(data.preparedBy || ANANTA_INFO.defaultPreparedBy);
        setClientName(data.clientName || ''); setClientCompanyName(data.clientCompanyName || '');
        setClientAddress(data.clientAddress || ''); setClientPhone(data.clientPhone || ''); setClientEmail(data.clientEmail || '');
        setLineItems(data.lineItems?.length ? data.lineItems : [{ id: uuidv4(), description: '', quantity: '1', rate: '' }]);
        setGstRate(data.gstRate || '18');
        setTermsValidity(data.termsValidity || '15 Days'); setTermsDelivery(data.termsDelivery || 'Within 10 business days after confirmation');
        setTermsPayment(data.termsPayment || '50% advance, 50% before delivery'); setTermsShipping(data.termsShipping || 'Included');
        setAuthorizedName(data.authorizedName || ANANTA_INFO.defaultSignatoryName); setAuthorizedDesignation(data.authorizedDesignation || ANANTA_INFO.defaultSignatoryDesignation);
    };

    const handleDelete = (idToDelete) => {
        if (window.confirm('Are you sure?')) {
            setSavedInvoices(prev => prev.filter(inv => inv.id !== idToDelete));
            if (id === idToDelete) resetForm();
        }
    };
    
    const resetForm = () => handleLoad({});

    const handleDownloadPdf = () => {
        const input = invoiceRef.current;
        const noPrintElements = input.querySelectorAll('.no-print');
        noPrintElements.forEach(el => el.style.visibility = 'hidden');

        html2canvas(input, { scale: 3, useCORS: true, logging: true })
            .then((canvas) => {
                const imgData = canvas.toDataURL('image/png', 1.0);
                const pdf = new jsPDF('p', 'mm', 'a4');
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const imgProps = pdf.getImageProperties(imgData);
                const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
                let heightLeft = imgHeight;
                let position = 0;
                
                pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight, undefined, 'FAST');
                heightLeft -= pdf.internal.pageSize.getHeight();

                while (heightLeft > 0) {
                    position = -heightLeft;
                    pdf.addPage();
                    pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight, undefined, 'FAST');
                    heightLeft -= pdf.internal.pageSize.getHeight();
                }
                pdf.save(`Proforma-Invoice-${sanitizeFilename(clientName || invoiceNumber)}.pdf`);
            })
            .finally(() => {
                noPrintElements.forEach(el => el.style.visibility = 'visible');
            });
    };

    return (
        <div className="proforma-page">
            <div className="controls no-print">
                <h3>Controls</h3>
                <button onClick={handleSave}>Save</button>
                <button onClick={handleDownloadPdf}>Download PDF</button>
                <button onClick={resetForm} className="reset-btn">New / Reset</button>
            </div>

            {savedInvoices.length > 0 && (
                <div className="saved-invoices-panel no-print">
                   <h4>Saved Invoices</h4>
                     <ul>
                        {savedInvoices.sort((a,b) => new Date(b.savedAt) - new Date(a.savedAt)).map(inv => (
                            <li key={inv.id}>
                                <span>{inv.invoiceNumber || 'Untitled'} - <strong>{inv.clientName || 'N/A'}</strong></span>
                                <div className="saved-actions">
                                    <button onClick={() => handleLoad(inv)}>Load</button>
                                    <button className="delete-button" onClick={() => handleDelete(inv.id)}>Del</button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="proforma-container" ref={invoiceRef}>
                <header className="proforma-header">
                    <div className="company-info">
                        <h1>{ANANTA_INFO.companyName}</h1>
                        <p>{ANANTA_INFO.address}</p>
                        <p><strong>E:</strong> {ANANTA_INFO.email} | <strong>P:</strong> {ANANTA_INFO.contactNo} | <strong>W:</strong> {ANANTA_INFO.website}</p>
                    </div>
                    <div className="proforma-title-box">
                        <h2>Proforma Invoice</h2>
                    </div>
                </header>

                <section className="proforma-meta">
                    <div className="client-box">
                        <span className="meta-label">Bill To</span>
                        <input type="text" value={clientName} onChange={handleInputChange(setClientName)} placeholder="Client Name" className="input-large"/>
                        <input type="text" value={clientCompanyName} onChange={handleInputChange(setClientCompanyName)} placeholder="Client Company Name"/>
                        <textarea value={clientAddress} onChange={handleInputChange(setClientAddress)} placeholder="Client Address, City, ZIP"></textarea>
                        <input type="text" value={clientPhone} onChange={handleInputChange(setClientPhone)} placeholder="Client Phone"/>
                        <input type="email" value={clientEmail} onChange={handleInputChange(setClientEmail)} placeholder="Client Email"/>
                    </div>
                    <div className="meta-box">
                        <div className="meta-item"><span className="meta-label">Invoice #</span><input type="text" value={invoiceNumber} onChange={handleInputChange(setInvoiceNumber)} placeholder="PI-2024-001"/></div>
                        <div className="meta-item"><span className="meta-label">Date</span><input type="date" value={invoiceDate} onChange={handleInputChange(setInvoiceDate)}/></div>
                        <div className="meta-item"><span className="meta-label">Expiry Date</span><input type="date" value={expiryDate} onChange={handleInputChange(setExpiryDate)}/></div>
                        <div className="meta-item"><span className="meta-label">Prepared By</span><input type="text" value={preparedBy} onChange={handleInputChange(setPreparedBy)}/></div>
                    </div>
                </section>
                
                <section className="proforma-items">
                    <table>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th className="desc-col">Description</th>
                                <th>Qty</th>
                                <th>Unit Price</th>
                                <th>Total</th>
                                <th className="no-print action-col"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.map((item, index) => (
                                <tr key={item.id}>
                                    <td>{index + 1}</td>
                                    <td><input type="text" value={item.description} onChange={handleLineItemChange(item.id, 'description')} placeholder="Service or Product Name"/></td>
                                    <td><input type="text" value={item.quantity} onChange={handleLineItemChange(item.id, 'quantity')} className="numeric-input"/></td>
                                    <td><input type="text" value={item.rate} onChange={handleLineItemChange(item.id, 'rate')} className="numeric-input" placeholder="0.00"/></td>
                                    <td className="amount-cell">{formatCurrency(Number(item.quantity || 0) * Number(item.rate || 0))}</td>
                                    <td className="no-print action-col">{lineItems.length > 1 && <button onClick={() => removeLineItem(item.id)} className="remove-item-btn">×</button>}</td>
                                </tr>
                            ))}
                              <tr className="no-print">
                                <td colSpan="6">
                                    <button onClick={addLineItem} className="add-item-btn">+ Add Line Item</button>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan="3"></td><td className="summary-label">Subtotal</td><td className="summary-value">{formatCurrency(subtotal)}</td><td className="no-print"></td>
                            </tr>
                            <tr>
                                <td colSpan="3"></td><td className="summary-label">GST @ <input type="text" value={gstRate} onChange={handleNumericInputChange(setGstRate)} className="gst-rate-input"/>%</td><td className="summary-value">{formatCurrency(gstAmount)}</td><td className="no-print"></td>
                            </tr>
                            <tr className="grand-total">
                                <td colSpan="3"></td><td className="summary-label">Total (INR)</td><td className="summary-value">{formatCurrency(grandTotal)}</td><td className="no-print"></td>
                            </tr>
                        </tfoot>
                    </table>
                </section>
                
                <footer className="proforma-footer">
                    <div className="terms-section">
                        <h4>Terms & Conditions</h4>
                        <ul>
                            <li>This is a Proforma Invoice and not a demand for payment.</li>
                            <li><strong>Validity:</strong> <input type="text" value={termsValidity} onChange={handleInputChange(setTermsValidity)} /></li>
                            <li><strong>Delivery:</strong> <input type="text" value={termsDelivery} onChange={handleInputChange(setTermsDelivery)} /></li>
                            <li><strong>Payment:</strong> <input type="text" value={termsPayment} onChange={handleInputChange(setTermsPayment)} /></li>
                            <li><strong>Shipping:</strong> <input type="text" value={termsShipping} onChange={handleInputChange(setTermsShipping)} /></li>
                        </ul>
                    </div>
                     <div className="bank-section">
                        <h4>Bank Details</h4>
                        <p><strong>A/c Name:</strong> {ANANTA_INFO.bank.holderName}</p>
                        <p><strong>Bank:</strong> {ANANTA_INFO.bank.bankName}</p>
                        <p><strong>A/c No:</strong> {ANANTA_INFO.bank.accountNo}</p>
                        <p><strong>IFSC:</strong> {ANANTA_INFO.bank.ifsc}</p>
                    </div>
                    <div className="signature-section">
                        <h4>Authorized Signature</h4>
                        <div className="signature-space"></div>
                        <input type="text" value={authorizedName} onChange={handleInputChange(setAuthorizedName)} placeholder="Signatory Name" className="signature-input"/>
                        <p className="signature-designation">{authorizedDesignation}</p>
                    </div>
                </footer>
            </div>
        </div>
    );
}

export default ProformaInvoice;